<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { SettingLevel } from '@/services/ai-disclosure/type/ai-disclosure.enum';
import type { GoalSettings } from '@/services/ai-disclosure/type/ai-disclosure.type';

const { t } = useI18n();
const props = defineProps<{ settings: GoalSettings }>();

const keys = [
    'applicationField',
    'inventionSubject',
    'techEffect',
    'techMeans',
    'other'
];

const descMiddleContentMap = {
    applicationField: t('aiDisclosure.applicationField'),
    inventionSubject: t('aiDisclosure.inventionSubject'),
    techEffect: t('aiDisclosure.techEffect'),
    techMeans: t('aiDisclosure.techMeans'),
    other: t('aiDisclosure.other'),
};

const labels = {
    [SettingLevel.WEEK]: t('aiDisclosure.settingLevelWeek'),
    [SettingLevel.MEDIUM]: t('aiDisclosure.settingLevelMedium'),
    [SettingLevel.GREAT]: t('aiDisclosure.settingLevelGreat'),
};

const level = ref(SettingLevel.WEEK);
const noContentKeys = ref([]);
const descMiddleContent = ref('');
const levelDesc = ref('');

const levelLabel = computed(() => {
    return labels[level.value];
});

const levelBarWidthClass = computed(() => {
    switch (level.value) {
    case SettingLevel.WEEK:
        return 'w-[33.33%]';
    case SettingLevel.GREAT:
        return 'w-full';
    default:
        return 'w-[66.66%]';
    }
});

function initLevel() {
    noContentKeys.value = [];
    keys.forEach(key => {
        if (!props.settings[key]?.length ?? 0) {
            noContentKeys.value.push(key);
        }
    });

    descMiddleContent.value = noContentKeys.value.map(key => descMiddleContentMap[key]).join(t('aiDisclosureGlobal.punctuation.comma'));

    switch (noContentKeys.value.length) {
    case 1:
    case 2:
        level.value = SettingLevel.MEDIUM;
        levelDesc.value = t('aiDisclosure.settingLevelMediumDesc', { content: descMiddleContent.value });
        break;
    case 0:
        level.value = SettingLevel.GREAT;
        levelDesc.value = t('aiDisclosure.settingLevelGreatDesc');
        break;
    default:
        level.value = SettingLevel.WEEK;
        levelDesc.value = t('aiDisclosure.settingLevelWeekDesc', { content: descMiddleContent.value });
    }
}

watch(() => props.settings, () => {
    initLevel();
}, {
    immediate: true,
    deep: true,
});
</script>

<template>
    <div class="setting-level rounded-lg px-2 py-3">
        <div class="h-1 w-full rounded-full bg-grey-30">
            <div class="setting-level-bar h-full rounded-full" :class="levelBarWidthClass" />
        </div>

        <div class="mt-1 flex justify-start text-xs">
            <div class="mr-1 font-semibold text-primary">
                {{ levelLabel }}
            </div>
            <div class="text-grey-450">
                {{ levelDesc }}
            </div>
        </div>
    </div>
</template>

<style lang="scss">
.setting-level {
    background: url('../../../assets/images/bg-disclosure-setting-level.png') no-repeat left, linear-gradient(90deg, #EDF4FC 0%, rgba(237, 244, 252, 0.2) 100%);
    background-size: auto 100%;

    &-bar {
        background: linear-gradient(270deg, #18EC81 0%, #0DACBA 42.43%, #0A3DFF 100%);
    }
}
</style>
